import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash, HandCoins } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PF, fmtEur } from './pfApi';
import { useConfirmDelete } from '@/components/ConfirmDialogs';

const empty = { description: '', amount: '', category_id: '', account_id: '', day_of_month: 1, is_shared: false, duarte_pct: 50 };

export default function PfRecurring({ categories, accounts }) {
  const confirmDelete = useConfirmDelete();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(empty);

  const fetchList = useCallback(async () => {
    try { const { data } = await axios.get(`${PF}/recurring`); setList(data); } catch {}
  }, []);
  useEffect(() => { fetchList(); }, [fetchList]);

  const create = async () => {
    const amount = parseFloat(String(form.amount).replace(',', '.'));
    if (!form.description.trim() || !amount) { toast.error('Descrição e valor são obrigatórios'); return; }
    try { await axios.post(`${PF}/recurring`, { ...form, amount }); toast.success('Despesa recorrente criada'); setForm(empty); fetchList(); }
    catch { toast.error('Erro ao criar'); }
  };
  const toggleActive = async (r) => { await axios.put(`${PF}/recurring/${r.id}`, { active: !r.active }); fetchList(); };
  const del = async (id) => {
    const r = list.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a despesa recorrente "${r?.description || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${PF}/recurring/${id}`); toast.success('Eliminada'); fetchList(); } catch { toast.error('Erro'); }
  };

  return (
    <div data-testid="pf-recurring-tab">
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
        <p className="text-sm font-medium text-brand-text mb-3">Nova despesa recorrente</p>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Descrição (ex: Ginásio)" className="rounded-xl border-brand-border" data-testid="pf-rec-description" />
          <Input inputMode="decimal" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="Valor €" className="rounded-xl border-brand-border" data-testid="pf-rec-amount" />
        </div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <Select value={form.category_id} onValueChange={v => setForm(f => ({ ...f, category_id: v }))}>
            <SelectTrigger className="rounded-xl border-brand-border" data-testid="pf-rec-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>{categories.filter(c => c.type === 'despesa').map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.account_id} onValueChange={v => setForm(f => ({ ...f, account_id: v }))}>
            <SelectTrigger className="rounded-xl border-brand-border" data-testid="pf-rec-account"><SelectValue placeholder="Conta" /></SelectTrigger>
            <SelectContent>{accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div>
            <Label className="text-xs text-brand-muted mb-1 block">Dia do mês</Label>
            <Input type="number" min="1" max="28" value={form.day_of_month} onChange={e => setForm(f => ({ ...f, day_of_month: e.target.value }))} className="rounded-xl border-brand-border w-20" data-testid="pf-rec-day" />
          </div>
          <label className="flex items-center gap-2 text-sm text-brand-text cursor-pointer mt-4">
            <input type="checkbox" checked={form.is_shared} onChange={e => setForm(f => ({ ...f, is_shared: e.target.checked }))} data-testid="pf-rec-is-shared" /> Partilhada com o Duarte
          </label>
        </div>
        <button onClick={create} className="w-full bg-brand-green text-white rounded-full py-2.5 text-sm font-medium flex items-center justify-center gap-2" data-testid="pf-rec-create-btn"><Plus size={15} /> Criar recorrente</button>
      </div>

      <div className="space-y-1.5">
        {list.map(r => (
          <div key={r.id} className="bg-white border border-brand-border rounded-xl p-3 flex items-center justify-between gap-2" data-testid={`pf-rec-${r.id}`}>
            <div className="min-w-0">
              <p className="text-sm text-brand-text truncate flex items-center gap-1.5">{r.description}{r.is_shared && <HandCoins size={12} className="text-amber-600" />}</p>
              <p className="text-[11px] text-brand-muted">dia {r.day_of_month} · {fmtEur(r.amount)}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => toggleActive(r)} className={`text-[10px] font-medium px-2 py-1 rounded-full ${r.active ? 'bg-brand-green/10 text-brand-green' : 'bg-brand-bg text-brand-muted'}`} data-testid={`pf-rec-toggle-${r.id}`}>{r.active ? 'Ativa' : 'Pausada'}</button>
              <button onClick={() => del(r.id)} className="text-brand-placeholder hover:text-red-500 p-1" data-testid={`pf-rec-delete-${r.id}`}><Trash size={14} /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-brand-placeholder">Sem despesas recorrentes.</p>}
      </div>
    </div>
  );
}
